import blogService from "@/services/api/blog.service";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
function arrayBufferToBase64(buffer) {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}
const CreateBlog = ({
  onCancel,
  onSuccess,
  isEditing = false,
  blogData = null,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [preview, setPreview] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  // Initialize form data with existing post data if editing
  const [formData, setFormData] = useState(() => {
    if (isEditing && blogData) {
      return {
        title: blogData.title || "",
        content: blogData.content || "",
        excerpt: blogData.excerpt || "",
        status: blogData.status || "draft",
        categoryId: blogData.categories?.[0]?._id || "",
        tags: blogData.tags?.map((tag) => tag._id) || [],
        featuredImage: blogData.featuredImage || null,
      };
    }
    return {
      title: "",
      content: "",
      excerpt: "",
      status: "draft",
      categoryId: "",
      tags: [],
      featuredImage: null,
    };
  });

  // Set preview if featured image exists in blog data
  useEffect(() => {
    if (isEditing && blogData?.featuredImage?.data) {
      const base64String = arrayBufferToBase64(blogData.featuredImage.data);
      const contentType = blogData.featuredImage.contentType || "image/jpeg";
      setPreview(`data:${contentType};base64,${base64String}`);
      setFormData((prev) => ({
        ...prev,
        featuredImage: blogData.featuredImage,
      }));
    }
  }, [isEditing, blogData]);
  useEffect(() => {
    if (isEditing && blogData?.tags) {
      setSelectedTags(blogData.tags.map((tag) => tag._id));
      setFormData((prev) => ({
        ...prev,
        tags: blogData.tags.map((tag) => tag._id),
      }));
    }
  }, [isEditing, blogData]);

  useEffect(() => {
    const fetchCategoriesAndTags = async () => {
      try {
        const categoriesData = await blogService.getCategories();
        setCategories(categoriesData.data || []);

        const tagsData = await blogService.getTags();
        setTags(tagsData.data || []);
      } catch (error) {
        toast.error("Failed to load categories and tags");
      }
    };

    fetchCategoriesAndTags();

    // Rest of your code remains the same
    const savedData = blogService.getSavedBlogData();
    if (savedData && !isEditing) {
      // Add !isEditing condition
      setFormData((prevData) => ({
        ...prevData,
        ...savedData,
      }));

      if (savedData.tags) {
        setSelectedTags(savedData.tags);
      }
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    // Save form data to localStorage as user types
    blogService.saveBlogData({
      ...formData,
      [name]: value,
    });
  };

  const handleContentChange = (e) => {
    const content = e.target.value;
    setFormData((prevData) => ({
      ...prevData,
      content,
    }));

    // Auto-generate excerpt if it's empty
    if (!formData.excerpt.trim() && content.length > 150) {
      const excerpt = content.substring(0, 150).trim() + "...";
      setFormData((prevData) => ({
        ...prevData,
        excerpt,
      }));
    }

    // Save to localStorage
    blogService.saveBlogData({
      ...formData,
      content,
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prevData) => ({
        ...prevData,
        featuredImage: file,
      }));

      // Create image preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleTagChange = (e) => {
    const tagId = e.target.value;
    const isChecked = e.target.checked;
    let updatedTags;
    if (isChecked) {
      updatedTags = [...selectedTags, tagId];
    } else {
      updatedTags = selectedTags.filter((id) => id !== tagId);
    }

    setSelectedTags((prevTags) => {
      const newTags = isChecked
        ? [...prevTags, tagId]
        : prevTags.filter((id) => id !== tagId);
      setFormData((prevFormData) => ({
        ...prevFormData,
        tags: newTags,
      }));

      // Save updated data
      blogService.saveBlogData({ ...formData, tags: newTags });

      return newTags; // Return new state for React to update properly
    });
  };
  useEffect(() => {
    return () => {
      if (!isEditing) blogService.clearSavedBlogData();
    };
  }, []);
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.title.trim()) {
      toast.error("Title is required");
      return;
    }

    if (!formData.content.trim()) {
      toast.error("Content is required");
      return;
    }

    if (!formData.categoryId) {
      toast.error("Please select a category");
      return;
    }

    try {
      setLoading(true);
      let response;

      const postData = {
        ...formData,
        tags: selectedTags,
      };

      if (isEditing) {
    
        response = await blogService.updatePost(blogData._id, postData);
        toast.success("Blog post updated successfully!");
      } else {
        response = await blogService.createPost(postData);
        toast.success("Blog post created successfully!");
      }

      if (onSuccess) {
        onSuccess(response.data); // Pass the created/updated blog data back
      }
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(
        isEditing
          ? "Failed to update blog post."
          : "Failed to create blog post."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    // Save as draft
    try {
      setLoading(true);
      const draftData = {
        ...formData,
        status: "draft",
      };

      const response = await blogService.createPost(draftData);

      toast.success("Draft saved successfully!");
      // IMPORTANT: Clear all form data before navigation
      setFormData({
        title: "",
        content: "",
        excerpt: "",
        status: "draft",
        categoryId: "",
        tags: [],
        featuredImage: null,
      });
      setSelectedTags([]);
      setPreview(null);
      blogService.clearSavedBlogData();
      navigate(`/dashboard/blogs/edit/${response.data.slug}`);
    } catch (error) {
      // console.error('Error saving draft:', error);
      toast.error("Failed to save draft. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white  rounded-lg p-6">
      <h1 className="text-2xl font-semibold text-gray-800 mb-6">
        {isEditing ? "Edit Blog Post" : "Create New Blog Post"}
      </h1>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Main content - 2/3 */}
          <div className="md:col-span-2 space-y-6">
            <div>
              <label
                htmlFor="title"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Title *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter blog title"
                required
              />
            </div>

            <div>
              <label
                htmlFor="content"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Content *
              </label>
              <textarea
                id="content"
                name="content"
                value={formData.content}
                onChange={handleContentChange}
                rows="12"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Write your blog content here..."
                required
              ></textarea>
              <p className="text-sm text-gray-500 mt-1">
                {formData.content.length} characters
              </p>
            </div>

            <div>
              <label
                htmlFor="excerpt"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Excerpt
              </label>
              <textarea
                id="excerpt"
                name="excerpt"
                value={formData.excerpt}
                onChange={handleChange}
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Brief summary of your blog post (will be auto-generated if left empty)"
              ></textarea>
              <p className="text-sm text-gray-500 mt-1">
                {formData.excerpt.length} / 200 characters
              </p>
            </div>
          </div>

          {/* Sidebar - 1/3 */}
          <div className="space-y-6">
            <div>
              <label
                htmlFor="status"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="categoryId"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Category *
              </label>
              <select
                id="categoryId"
                name="categoryId"
                value={formData.categoryId}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select a category</option>
                {categories.map((category) => (
                  <option key={category._id} value={category._id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <span className="block text-sm font-medium text-gray-700 mb-2">
                Tags
              </span>
              <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-md p-3">
                {tags.length === 0 ? (
                  <p className="text-gray-500 text-sm">No tags available</p>
                ) : (
                  tags.map((tag) => (
                    <div key={tag._id} className="tag-checkbox">
                      <input
                        type="checkbox"
                        id={`tag-${tag._id}`}
                        value={tag._id}
                        checked={
                          selectedTags.includes(String(tag._id)) ||
                          selectedTags.includes(Number(tag._id))
                        }
                        onChange={handleTagChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        style={{ position: "relative", zIndex: 10 }}
                      />
                      <label htmlFor={`tag-${tag._id}`}>{tag.name}</label>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Featured Image
              </label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                {preview ? (
                  <div className="text-center">
                    <img
                      src={preview}
                      alt="Preview"
                      className="h-32 object-cover mx-auto mb-2 rounded"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setPreview(null);
                        setFormData((prev) => ({
                          ...prev,
                          featuredImage: null,
                        }));
                      }}
                      className="text-sm text-red-600 hover:text-red-900"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="space-y-1 text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                      aria-hidden="true"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="featuredImage"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                      >
                        <span>Upload a file</span>
                        <input
                          id="featuredImage"
                          name="featuredImage"
                          type="file"
                          className="sr-only"
                          accept="image/*"
                          onChange={handleImageChange}
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      PNG, JPG, GIF up to 5MB
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex justify-end space-x-3">
          <button
            type="button"
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            onClick={() => {
              // Clear form data
              setFormData({
                title: "",
                content: "",
                excerpt: "",
                status: "draft",
                categoryId: "",
                tags: [],
                featuredImage: null,
              });
              setSelectedTags([]);
              setPreview(null);
              blogService.clearSavedBlogData();
              // Only call onCancel (which is passed by Posts.jsx)
              if (onCancel) {
                onCancel();
              }
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            onClick={handleSaveDraft}
            disabled={loading}
          >
            {loading ? "Saving..." : "Save Draft"}
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            disabled={loading}
          >
            {loading
              ? isEditing
                ? "Updating..."
                : "Publishing..."
              : isEditing
              ? "Update"
              : "Publish"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateBlog;
